import { Color } from '@persona-id/design-tokens';
import { useEffect, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { connect, ConnectedProps } from 'react-redux';
import styled from 'styled-components';

import GraphColoringRulesField from 'dashboard/components/FormControls/GraphColoringRulesField';
import GraphCreateColorRuleField from 'dashboard/components/FormControls/GraphCreateColorRuleField/GraphCreateColorRuleField';
import GraphNodeConfigurationRow from 'dashboard/components/FormControls/GraphNodeConfigurationRow/GraphNodeConfigurationRow';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavConfirmPrompt from 'dashboard/components-lib/NavConfirmPrompt';
import Form from 'dashboard/layouts/FormLayout/Form';
import { Container, Header, Section as LayoutSection } from 'dashboard/layouts/IndexViewLayoutV2';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { AccountType } from 'dashboard/models/account-type';
import {
  ColorRule,
  DatumType,
  DatumTypeToken,
  GraphAttributes,
  NodeConditions,
} from 'dashboard/models/graph';
import {
  ServerColorRuleCondition,
  ServerColorRuleStatus,
  ServerColorRuleTag,
  ServerGraphProperty,
  ServerNodeRenderConfig,
  ServerPresetSummary,
} from 'dashboard/models/graph-backend';
import { GraphDatumTypeAttributes } from 'dashboard/models/graph-datum-type';
import { showMessage } from 'dashboard/redux/actions/messages';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useToggle from 'lib/hooks/useToggle';
import { useFormSecurely } from 'lib/security/reactHookForm';
import { assertNever } from 'lib/utils/types';

import FormCreateGraphNode from '../FormCreateGraphNode/FormCreateGraphNode';

interface FormGraphConfigurationProps {
  accountTypes: AccountType[];
  onSubmit: (config: GraphAttributes) => Promise<void>;
  handleCreateGraphDatumType: (config: GraphDatumTypeAttributes) => Promise<void>;
  initialValues: GraphAttributes;
  isSubmittingNewNodeForm: boolean;
  presetSummaries: ServerPresetSummary[];
}

const ModalContainerOverflowable = styled(ModalContainer)`
  overflow-y: visible;
`;

const ModalBodyOverflowable = styled(ModalBody)`
  overflow-y: visible;
`;

const CardBody = styled(Card.Body)`
  grid-gap: unset;
`;

const FormGraphConfiguration = ({
  onSubmit,
  initialValues,
  handleCreateGraphDatumType,
  isSubmittingNewNodeForm,
  accountTypes,
  presetSummaries,
  ...props
}: FormGraphConfigurationProps & PropsFromRedux) => {
  const standardNodeConfigurationRows: JSX.Element[] = [];
  const customNodeConfigurationRows: JSX.Element[] = [];
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [isNewNodeModalOpen, setIsNewNodeModalOpen] = useState(false);
  const { handleSubmit, control, setValue, watch } = useFormSecurely<GraphAttributes>({
    defaultValues: initialValues,
  });

  useEffect(() => {
    setValue('node-render-configs', initialValues['node-render-configs']);
  }, [initialValues, setValue]);

  const onSubmitForm = (config: GraphAttributes) => {
    onSubmit(config).then(() => {
      setDisableSubmit(true);
    });
  };

  const onGraphConfigEdit = () => {
    setDisableSubmit(false);
  };

  const emailSimilarityEnabled = useFeatureFlag('dashboard:graph-email-similarity');
  const nodeFunctionsWriteEnabled = useFeatureFlag(
    'dashboard:graph-config-node-functions-tab-enable-write',
  );

  const [
    isCreateColorRuleModalOpen,
    { enable: showCreateColorRuleModal, disable: hideCreateColorRuleModal },
  ] = useToggle(false);

  useFieldArray<GraphAttributes>({
    control,
    name: 'node-render-configs',
  });
  const { append, move, remove } = useFieldArray<GraphAttributes>({
    control,
    name: 'coloring-rules',
  });

  const nodeFunctionsConfigs = watch('node-functions-configs');
  const renderConfigFields = watch('node-render-configs');
  const coloringRuleFields = watch('coloring-rules');
  const customDatumTypes: Map<DatumTypeToken, DatumType> = new Map(
    initialValues['node-types'].map((serverNodeDatumType) => {
      const datumType: DatumType = {
        token: serverNodeDatumType['datum-type-token'],
        isCustomType: true,
        name: serverNodeDatumType.name,
        valueType: serverNodeDatumType['value-type'],
      };
      return [serverNodeDatumType['datum-type-token'], datumType];
    }),
  );

  renderConfigFields.map((field, index) => {
    const datumToken: string = field['datum-type-token'].replace('_', '-');
    const defaultGraphProperties =
      (initialValues['default-graph-properties'][datumToken] as ServerGraphProperty[]) || [];
    const configurationRow = (
      <GraphNodeConfigurationRow
        key={index}
        hasNodeFunctionsConfig={
          !!nodeFunctionsConfigs.find(
            (config) => config['datum-type-token'] === field['datum-type-token'],
          )
        }
        config={field as ServerNodeRenderConfig}
        defaultGraphProperties={defaultGraphProperties}
        index={index}
        setValue={setValue}
        customDatumTypes={customDatumTypes}
        onGraphConfigEdit={onGraphConfigEdit}
      />
    );
    if (!customDatumTypes.has(field['datum-type-token'])) {
      standardNodeConfigurationRows.push(configurationRow);
    } else {
      customNodeConfigurationRows.push(configurationRow);
    }
  });

  // Either creates a ColorRuleTag or a ColorRuleStatus or a ColorRuleCondition
  const handleCreateGraphColorRuleSubmit = (type: ColorRule['type'], colorRuleValue: string) => {
    const isDuplicateField = coloringRuleFields.some((field) => {
      if (field.type !== type) return false;
      switch (type) {
        case 'tag': {
          return (field as ServerColorRuleTag).tag === colorRuleValue;
        }
        case 'status': {
          return (field as ServerColorRuleStatus).status === colorRuleValue;
        }
        case 'condition': {
          return (field as ServerColorRuleCondition).condition === colorRuleValue;
        }
        default:
          assertNever(type);
      }
    });

    if (isDuplicateField) {
      props.showMessage(
        'Account coloring rule with this value already exists.',
        MessageType.Failure,
      );
      return;
    }
    switch (type) {
      case 'tag': {
        append({
          type: 'tag',
          tag: colorRuleValue,
          color: Color.CorePurple600,
        });
        break;
      }
      case 'status': {
        append({
          type: 'status',
          status: colorRuleValue,
          color: Color.CorePurple600,
        });
        break;
      }
      case 'condition': {
        append({
          type: 'condition',
          condition: colorRuleValue,
          color:
            colorRuleValue === NodeConditions.SeedSet ? Color.CorePurple900 : Color.CorePurple200,
        });
        break;
      }
      default:
        assertNever(type);
    }
    hideCreateColorRuleModal();
    onGraphConfigEdit();
  };

  return (
    <Form id='graph-configuration-form' onSubmit={handleSubmit(onSubmitForm)}>
      <NavConfirmPrompt
        prevent={!disableSubmit}
        confirmActionText='Leave'
        message='Are you sure you want to leave?'
      >
        You have unsaved changes.
      </NavConfirmPrompt>
      <Header title='Graph Configuration'>
        <Button
          type='submit'
          width='auto'
          data-test='form-graph-config__submit'
          disabled={disableSubmit}
        >
          Save
        </Button>
      </Header>
      <Container>
        <LayoutSection>
          <Card>
            <Card.Body>
              <Card.Title
                actions={
                  <Button
                    width='auto'
                    onClick={() => showCreateColorRuleModal()}
                    id='graph__create_color_rule'
                  >
                    Create
                  </Button>
                }
              >
                Account Coloring
              </Card.Title>
              <ModalWrapper
                isOpen={isCreateColorRuleModalOpen}
                handleCloseModal={hideCreateColorRuleModal}
                shouldCloseOnEsc={true}
                shouldCloseOnOverlayClick={true}
              >
                <ModalContainerOverflowable data-test='graph-explorer__template-save-modal'>
                  <ModalHeader onClose={hideCreateColorRuleModal}>
                    Create a new account color
                  </ModalHeader>

                  <ModalBodyOverflowable>
                    <GraphCreateColorRuleField onSubmit={handleCreateGraphColorRuleSubmit} />
                  </ModalBodyOverflowable>
                </ModalContainerOverflowable>
              </ModalWrapper>

              <p>
                <i>
                  Accounts that satisfy the following conditions will be highlighted with the
                  coloring rules below. Drag to order the priority of the coloring rules from top
                  (highest priority) to bottom (lowest priority).
                </i>
              </p>
              <GraphColoringRulesField
                coloringRules={coloringRuleFields}
                move={move}
                onGraphConfigEdit={onGraphConfigEdit}
                setValue={setValue}
                removeColorRule={remove}
              />
            </Card.Body>
          </Card>
          <ModalWrapper
            handleCloseModal={() => setIsNewNodeModalOpen(false)}
            isOpen={isNewNodeModalOpen}
            shouldCloseOnEsc
            shouldCloseOnOverlayClick
            styleOverride={{
              content: {
                overflow: 'hidden',
              },
            }}
          >
            <FormCreateGraphNode
              accountTypes={accountTypes}
              presetSummaries={presetSummaries}
              onSubmit={(data) => handleCreateGraphDatumType(data)}
              setIsModalOpen={setIsNewNodeModalOpen}
            />
          </ModalWrapper>
          <Card>
            <CardBody>
              <Card.Title
                actions={
                  emailSimilarityEnabled || nodeFunctionsWriteEnabled ? (
                    <Button
                      onClick={() => setIsNewNodeModalOpen(true)}
                      id='form-graph-configuration__add-graph-node'
                    >
                      Add
                    </Button>
                  ) : null
                }
              >
                Nodes
              </Card.Title>
              <p>
                <i>Nodes are displayed as shared connections between Account nodes.</i>
              </p>
              {standardNodeConfigurationRows}
              {customNodeConfigurationRows.length ? <h4>Custom</h4> : null}
              {customNodeConfigurationRows}
            </CardBody>
          </Card>
        </LayoutSection>
      </Container>
    </Form>
  );
};

const mapDispatchToProps = {
  showMessage,
};

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(FormGraphConfiguration);
